import type { FtueQuery } from '@/types';

export interface FtueFailure {
  /** The query that failed, so Retry asks for the same thing. */
  query: FtueQuery;
  message: string;
}

export interface FtuePageState {
  /** The query the board on screen was answered for; null until a first load succeeds. */
  shown: FtueQuery | null;
  /** What the filter bar holds. It equals `shown` unless a reload is under way. */
  query: FtueQuery;
  /** The last reload that failed, until a load succeeds. */
  failure: FtueFailure | null;
  /**
   * The filter bar's React key. The bar keeps its own compare mode and value after mounting,
   * so it is remounted (key bumped) whenever `query` is changed from outside it.
   */
  barKey: number;
}

export type FtuePageAction =
  /** The user changed a filter: the bar already shows it, the request is on its way. */
  | { type: 'start'; query: FtueQuery }
  /** The newest request succeeded; `answered` is its query with the primary filter the server used. */
  | { type: 'success'; answered: FtueQuery }
  /** The newest request failed. */
  | { type: 'failure'; attempted: FtueQuery; message: string };

export const initialFtuePageState: FtuePageState = {
  shown: null,
  query: {},
  failure: null,
  barKey: 0,
};

const KEYS: Array<keyof FtueQuery> = [
  'from',
  'to',
  'version',
  'platform',
  'compareFrom',
  'compareTo',
  'compareVersion',
  'comparePlatform',
];

/** True when both queries ask for the same thing (an absent key equals an undefined one). */
export function sameQuery(a: FtueQuery, b: FtueQuery): boolean {
  return KEYS.every((k) => a[k] === b[k]);
}

/**
 * The page's query and error state. Rules, so the bar and the board never disagree silently:
 * - a failure puts the bar back to the last good query (and remounts it);
 * - a success remounts the bar after a failure, or when the answered query is not what the bar
 *   holds (the server changed a value, or the bar was put back while this request ran), and
 *   otherwise leaves it alone so a normal reload does not reset the open controls.
 * Retry and Refresh dispatch nothing before the request: the bar keeps showing the last good
 * query, which is what the board shows, until the answer arrives.
 */
export function ftuePageReducer(state: FtuePageState, action: FtuePageAction): FtuePageState {
  switch (action.type) {
    case 'start':
      return { ...state, query: action.query };
    case 'success':
      return {
        shown: action.answered,
        query: action.answered,
        failure: null,
        barKey:
          state.failure !== null || !sameQuery(state.query, action.answered)
            ? state.barKey + 1
            : state.barKey,
      };
    case 'failure':
      return {
        ...state,
        failure: { query: action.attempted, message: action.message },
        // With no good load yet there is no bar to put back.
        query: state.shown ?? state.query,
        barKey: state.shown ? state.barKey + 1 : state.barKey,
      };
  }
}
